import * as XLSX from 'xlsx';
import { FileResultType } from '../../../Helper/file';
import { BulkExtraChargeShipment, ExtraChargeItem } from './types';

export const readXslAndGetBulkExtraChargesUploadObject = (data: FileResultType[]): BulkExtraChargeShipment[] => {
  if (!data?.length) {
    throw { msg: 'Please upload Extra Charges sheet.' };
  }

  const file = data[0];

  // Check if fileData exists
  if (!file.fileData || !(file.fileData instanceof Array) || file.fileData.length === 0) {
    throw { msg: 'File data is missing' };
  }

  const sheetData = file.fileData[0];

  // Convert array data into worksheet
  const worksheetData = sheetData.data;

  if (!worksheetData || worksheetData.length < 2) {
    throw { msg: 'Sheet is empty or has no data rows.' };
  }

  // Create worksheet object
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  // Convert to JSON
  const jsonData: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });

  if (!jsonData.length) {
    throw { msg: 'Sheet is empty.' };
  }

  const getRowValue = (row: any, keys: string[]) => {
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
        return row[key].toString().trim();
      }
    }
    const rowKeys = Object.keys(row);
    for (const targetKey of keys) {
      const matchedKey = rowKeys.find(k => k.trim().toLowerCase() === targetKey.toLowerCase());
      if (matchedKey && row[matchedKey] !== undefined && row[matchedKey] !== null && row[matchedKey] !== '') {
        return row[matchedKey].toString().trim();
      }
    }
    return '';
  };

  const parseBooleanTax = (val: string): boolean => {
    const lower = val.toLowerCase().trim();
    if (lower === 'yes' || lower === 'true' || lower === '1') return true;
    if (lower === 'no' || lower === 'false' || lower === '0' || lower === '') return false;
    return false;
  };

  const shipmentMap = new Map<string, ExtraChargeItem[]>();

  jsonData.forEach((row, index) => {
    const rowNum = index + 2;
    const shipmentNumber = getRowValue(row, ['Shipment Number', 'shipmentNumber', 'Shipment number', 'Shipment']);
    const chargeType = getRowValue(row, ['Charge Type', 'chargeType', 'Charge type', 'type', 'Type']);
    const chargeName = getRowValue(row, ['Charge Name', 'chargeName', 'Charge name', 'name', 'Name']);
    const rawValue = getRowValue(row, ['Value', 'value', 'Amount', 'amount']);
    const rawApplyTax = getRowValue(row, ['Apply Tax', 'applyTax', 'Apply tax', 'isTaxed', 'Tax', 'tax']);

    // Skip completely empty rows
    if (!shipmentNumber && !chargeType && !chargeName && !rawValue && !rawApplyTax) {
      return;
    }

    if (!shipmentNumber) {
      throw { msg: `Shipment Number is missing at row ${rowNum}` };
    }
    if (!chargeType) {
      throw { msg: `Charge Type is missing for shipment ${shipmentNumber} at row ${rowNum}` };
    }
    if (!chargeName) {
      throw { msg: `Charge Name is missing for shipment ${shipmentNumber} at row ${rowNum}` };
    }
    if (!rawValue) {
      throw { msg: `Value is missing for shipment ${shipmentNumber} at row ${rowNum}` };
    }

    const numericValue = Number(rawValue);
    if (isNaN(numericValue)) {
      throw { msg: `Invalid numeric Value "${rawValue}" for shipment ${shipmentNumber} at row ${rowNum}` };
    }

    // Normalize type to lowercase (e.g. add, discount)
    const normalizedType = chargeType.toLowerCase().trim();

    const isTaxed = parseBooleanTax(rawApplyTax);

    const chargeItem: ExtraChargeItem = {
      type: normalizedType,
      name: chargeName,
      value: numericValue,
      isTaxed,
    };

    if (!shipmentMap.has(shipmentNumber)) {
      shipmentMap.set(shipmentNumber, []);
    }
    shipmentMap.get(shipmentNumber)!.push(chargeItem);
  });

  const parsedShipments: BulkExtraChargeShipment[] = Array.from(shipmentMap.entries()).map(
    ([shipmentNumber, charges]) => ({
      shipmentNumber,
      charges,
    }),
  );

  if (!parsedShipments.length) {
    throw { msg: 'No valid shipment charges found in sheet.' };
  }

  if (parsedShipments.length > 500) {
    throw {
      msg: 'You can upload a maximum of 500 shipments at a time. Please reduce the number of shipments and try again.',
    };
  }

  return parsedShipments;
};
